<template>
	<AtomForm
		v-if="!isFormSent"
		:action="webhook"
		:is-submitting="isFormSubmitting"
		:is-success="isFormSent"
		:is-error="isFormFailed"
		:submit-button-text="submitButtonText"
		:submit-button-icon="submitButtonIcon"
		:is-next-step-disabled="isNextStepDisabled"
		@submit="submit"
	>
		<div class="grid grid-cols-2 gap-x-8">
			<AtomInputText
				v-model="firstName"
				name="first-name"
				title="Voornaam"
				is-required
			/>
			<AtomInputText
				v-model="lastName"
				name="last-name"
				title="Achternaam"
				is-required
			/>
			<AtomInputEmail
				v-model="email"
				class="col-span-2"
				name="email"
				title="E-mailadres"
				is-required
			/>
			<AtomInputPhone
				v-model="phone"
				class="col-span-2"
				name="phone"
				title="Telefoonnummer"
				is-required
			/>
		</div>
	</AtomForm>
	<Confirmation
		v-if="isFormSent"
		:email
		:first-name
		:last-name
	/>
</template>

<script lang="ts" setup>
import Confirmation from './FormLeadConfirmation.vue';
import isValidEmailAddress from '#base/helpers/validators/isValidEmailAddress';
import isValidPhoneNumber from '#base/helpers/validators/isValidPhoneNumber';
import leadFormSubmit from '#base/helpers/leadFormSubmit';
import type { LeadFormWebhooksEnum } from '#fit/core/enums/LeadFormWebhooksEnum';
import type { FontAwesomeIconType } from '#base/app/plugins/fontawesome';
import type { LeadFormProductsEnum } from '#fit/core/enums/LeadFormProductsEnum';

const {
	webhook,
	formName,
	conversionValue,
	productName,
	submitButtonText,
	submitButtonIcon,
} = defineProps<{
	webhook: LeadFormWebhooksEnum;
	formName: string;
	conversionValue: number;
	productName: LeadFormProductsEnum;
	submitButtonText?: string;
	submitButtonIcon?: FontAwesomeIconType;
}>();

const eventTracking = useEventTracking();

const firstName = ref<string>();
const lastName = ref<string>();
const email = ref<string>();
const phone = ref<string>();

const isFormSent = ref(false);
const isFormFailed = ref(false);
const isFormSubmitting = ref(false);

watch([firstName, lastName, email, phone], () => {
	if (!isFormSubmitting.value) {
		isFormSent.value = false;
		isFormFailed.value = false;
		isFormSubmitting.value = false;
	}
});

const isNextStepDisabled = computed(() => {
	return !firstName.value
		|| !lastName.value
		|| !email.value || !isValidEmailAddress(email.value)
		|| !phone.value || !isValidPhoneNumber(phone.value);
});

function submit(e: FormDataEvent) {
	e.preventDefault();

	isFormSubmitting.value = true;

	eventTracking.trackLeadFormSubmit(
		formName,
		conversionValue,
	);

	leadFormSubmit(webhook, {
		firstName: firstName.value ?? '-',
		lastName: lastName.value ?? '-',
		email: email.value ?? '-',
		phone: phone.value ?? '-',
		product: productName,
	})
		.then(() => {
			isFormSent.value = true;
			isFormFailed.value = false;
		})
		.catch(() => {
			isFormSent.value = false;
			isFormFailed.value = true;
		})
		.finally(() => {
			isFormSubmitting.value = false;
		});
}
</script>
